import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FC } from 'react';

import useAuth from 'src/hooks/useAuth';

import {
  LAST_ACTIVITY_AT_STORAGE_KEY,
  type DurationLike,
  durationToMilliseconds,
  readLastActivityAt,
  shouldLogoutForInactivity,
  writeLastActivityAt,
} from 'src/utils/sessionInactivity';

const ACTIVITY_WRITE_THROTTLE_MS = 30_000;
const INACTIVITY_CHECK_INTERVAL_MS = 30_000;

const DOCUMENT_ACTIVITY_EVENTS = ['click', 'keydown', 'mousedown', 'mousemove', 'scroll', 'touchstart'];
const WINDOW_ACTIVITY_EVENTS = ['focus'];

type Props = {
  timeoutDuration?: DurationLike | null;
};

/**
 * Logs the user out once the org-configured inactivity window has elapsed.
 * Activity is stored in localStorage so multiple app tabs share a single idle timer.
 */
const SessionInactivityTimeout: FC<Props> = ({ timeoutDuration }) => {
  const { isAuthenticated, logout } = useAuth();
  const lastActivityWriteAt = useRef(0);

  const inactivityTimeoutMs = useMemo(() => durationToMilliseconds(timeoutDuration), [timeoutDuration]);

  const writeCurrentActivity = useCallback((now: number) => {
    writeLastActivityAt(now);
    lastActivityWriteAt.current = now;
  }, []);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Registers browser activity and storage listeners while a timeout is configured.
  useEffect(() => {
    if (!isAuthenticated || !inactivityTimeoutMs) {
      return undefined;
    }

    let isLoggingOut = false;

    const logoutIfInactive = () => {
      if (isLoggingOut) {
        return true;
      }

      const now = Date.now();
      if (shouldLogoutForInactivity(now, readLastActivityAt(), inactivityTimeoutMs)) {
        isLoggingOut = true;
        logout();
        return true;
      }

      return false;
    };

    const recordActivity = () => {
      if (logoutIfInactive()) {
        return;
      }

      const now = Date.now();
      if (now - lastActivityWriteAt.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        writeCurrentActivity(now);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        recordActivity();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LAST_ACTIVITY_AT_STORAGE_KEY) {
        return;
      }

      if (event.newValue === null) {
        isLoggingOut = true;
        logout();
        return;
      }

      logoutIfInactive();
    };

    if (!readLastActivityAt()) {
      isLoggingOut = true;
      logout();
      return undefined;
    }

    if (logoutIfInactive()) {
      return undefined;
    }

    const eventOptions: AddEventListenerOptions = { passive: true };
    DOCUMENT_ACTIVITY_EVENTS.forEach((eventName) => document.addEventListener(eventName, recordActivity, eventOptions));
    WINDOW_ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, recordActivity, eventOptions));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);

    const interval = window.setInterval(logoutIfInactive, INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      DOCUMENT_ACTIVITY_EVENTS.forEach((eventName) =>
        document.removeEventListener(eventName, recordActivity, eventOptions)
      );
      WINDOW_ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity, eventOptions)
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      window.clearInterval(interval);
    };
  }, [inactivityTimeoutMs, isAuthenticated, logout, writeCurrentActivity]);

  return null;
};

export default SessionInactivityTimeout;
